'use client'

import { useState } from 'react'
import { X } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth'

interface GuestFormData {
  id?: string
  name: string
  title: string
  company: string
  email: string
}

export interface GuestModalProps {
  isOpen: boolean
  onClose: () => void
  guest?: GuestFormData | null
  onSuccess?: () => void
}

function getInitialFormData(guest: GuestFormData | null | undefined): GuestFormData {
  if (guest) {
    return {
      id: guest.id,
      name: guest.name || '',
      title: guest.title || '',
      company: guest.company || '',
      email: guest.email || '',
    }
  }
  return {
    name: '',
    title: '',
    company: '',
    email: '',
  }
}

export function GuestModal({ isOpen, onClose, guest, onSuccess }: GuestModalProps) {
  // Generate a key to force form reset when modal opens or guest changes
  const modalKey = isOpen ? `${guest?.id || 'new'}-${isOpen}` : 'closed'

  if (!isOpen) return null

  return (
    <GuestModalContent
      key={modalKey}
      guest={guest}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  )
}

interface GuestModalContentProps {
  guest?: GuestFormData | null
  onClose: () => void
  onSuccess?: () => void
}

function GuestModalContent({ guest, onClose, onSuccess }: GuestModalContentProps) {
  const { membership } = useAuth()
  const supabase = createClient()
  const queryClient = useQueryClient()

  const isEditing = !!guest?.id

  // Initialize form data once when component mounts
  const [formData, setFormData] = useState<GuestFormData>(() => getInitialFormData(guest))

  const createGuestMutation = useMutation({
    mutationFn: async (data: GuestFormData) => {
      if (!membership?.team_id) {
        throw new Error('Unable to create guest. Please refresh the page and try again.')
      }

      const { data: newGuest, error } = await supabase
        .from('guests')
        .insert({
          team_id: membership.team_id,
          name: data.name.trim(),
          title: data.title.trim(),
          company: data.company.trim() || null,
          email: data.email.trim(),
          status: 'active',
        } as never)
        .select()
        .single()

      if (error) throw error

      // Increment guests_used counter
      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (user) {
        const { error: rpcError } = await supabase.rpc(
          'increment_guests_used' as never,
          { p_user_id: user.id } as never
        )
        if (rpcError) {
          console.error('Failed to increment guests counter:', rpcError)
          // Don't throw - guest was created successfully
        }
      }

      return newGuest
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] })
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      onSuccess?.()
      onClose()
    },
  })

  const updateGuestMutation = useMutation({
    mutationFn: async (data: GuestFormData) => {
      if (!data.id) throw new Error('No guest ID')

      const { data: updatedGuest, error } = await supabase
        .from('guests')
        .update({
          name: data.name.trim(),
          title: data.title.trim(),
          company: data.company.trim() || null,
          email: data.email.trim(),
        } as never)
        .eq('id', data.id)
        .select()
        .single()

      if (error) throw error
      return updatedGuest
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] })
      onSuccess?.()
      onClose()
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      if (isEditing) {
        await updateGuestMutation.mutateAsync(formData)
      } else {
        await createGuestMutation.mutateAsync(formData)
      }
    } catch {
      // Error is handled by mutation's isError state
    }
  }

  const handleInputChange = (field: keyof GuestFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const isSubmitting = createGuestMutation.isPending || updateGuestMutation.isPending
  const canSubmit = formData.name.trim() && formData.title.trim() && formData.email.trim()
  const hasError = createGuestMutation.isError || updateGuestMutation.isError

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        data-testid="guest-modal-backdrop"
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-md mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="guest-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {isEditing ? 'Edit Guest' : 'Add New Guest'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            data-testid="guest-modal-close"
          >
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            <div>
              <label
                htmlFor="guest-name"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
              >
                Name <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                id="guest-name"
                data-testid="guest-name-input"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Guest name"
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label
                htmlFor="guest-title"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
              >
                Title <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                id="guest-title"
                data-testid="guest-title-input"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="e.g., CEO, Author, Developer"
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label
                htmlFor="guest-company"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
              >
                Company
              </label>
              <input
                type="text"
                id="guest-company"
                data-testid="guest-company-input"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                placeholder="Company name"
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label
                htmlFor="guest-email"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
              >
                Email <span className="text-rose-500">*</span>
              </label>
              <input
                type="email"
                id="guest-email"
                data-testid="guest-email-input"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="guest@email.com"
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            {/* Error display */}
            {hasError && (
              <p className="text-sm text-rose-600 dark:text-rose-400" data-testid="guest-modal-error">
                Failed to {isEditing ? 'update' : 'create'} guest. Please try again.
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-200 dark:border-zinc-800">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
              data-testid="guest-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="guest-modal-submit"
            >
              {isSubmitting
                ? isEditing
                  ? 'Saving...'
                  : 'Creating...'
                : isEditing
                ? 'Save Changes'
                : 'Create Guest'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
